import {
  Component,
  OnInit,
  inject,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { effect } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormGroupDirective,
  FormControl,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { MatChipsModule } from "@angular/material/chips";
import {
  Subject,
  takeUntil,
  debounceTime,
  Observable,
  take,
  combineLatest,
} from "rxjs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MipsApiService } from "@services/mips-api.service";
import { openBlobInNewTab, downloadBlob } from "../../utils/blob-viewer.util";
import {
  selectActivePacket,
  selectProvidersForSelectedPractice,
  selectRelatedOrganizations,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { Store } from "@ngrx/store";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { map, startWith } from "rxjs/operators";
import { TinEligibilityDetails } from "@models/mips-stepper.model";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { selectUserAdminFilteredOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { UserService } from "@services/user.service";
import { RoleConstants, ROLE_GROUPS } from "../../constants/roles";
import { MAX_FILE_SIZE } from '@constants/file-upload';
import { selectEligibilityResult } from "@store/selectors/mips-eligibility/mips-eligibility.selectors";

@Component({
  selector: "app-mips-tin-eligibility-details",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatSelectModule,
    MatRadioModule,
    MatChipsModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxMatSelectSearchModule,
    MatCheckboxModule,
  ],
  templateUrl: "./mips-tin-eligibility-details.component.html",
  styleUrls: ["./mips-tin-eligibility-details.component.css"],
})
export class MipsTinEligibilityDetailsComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly mipsApi = inject(MipsApiService);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly userService = inject(UserService);
  private readonly destroy$ = new Subject<void>();
  private previousProviders: Array<string | number> = ["all"];
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly tinStatusEffect = effect(() => {
    const status = this.tinStatus;
    const packet = this.currentPacket();
    if (packet?.id && this.tinDetailsForm) {
      setTimeout(() => this.updateParentFormValidity(), 0);
    }
  });

  private previousPractice: string | undefined;

  tinDetailsForm!: FormGroup;
  parentForm!: FormGroup;
  uploadedFileName: string | null = null;
  showAdminActions: boolean = false;
  uploadingDocument: boolean = false;
  private currentPacketId: number | undefined;

  providerSearchControl = new FormControl("");
  filteredProviders$!: Observable<any[]>;

  currentPacket = toSignal(this.store.select(selectActivePacket));
  activeUser = toSignal(this.store.select(selectActiveUser));
  providers = toSignal(this.store.select(selectProvidersForSelectedPractice));
  relatedOrganizations = toSignal(this.store.select(selectRelatedOrganizations), { initialValue: [] });
  isSuperAdmin$ = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);
  eligibilityResult = toSignal(this.store.select(selectEligibilityResult));

  get performanceYear(): number {
    return this.currentPacket()?.performanceYear || 2025;
  }

  get npi(): string {
    return this.currentPacket()?.npi || "";
  }

  get reportingLevel(): string {
    return this.currentPacket()?.reportingLevel || "";
  }

  get reportingType(): string {
    return this.currentPacket()?.reportingType || "";
  }

  get practice(): string {
    const practiceId = this.currentPacket()?.practice || "";

    if (practiceId) {
      return practiceId;
    }

    return "";
  }

  get practiceOrgName(): string {
    const practiceId = this.currentPacket()?.practice || "";

    if (!practiceId) {
      return "";
    }

    let orgName = practiceId;

    this.store
      .select(selectUserAdminFilteredOrgs)
      .pipe(take(1))
      .subscribe((orgs) => {
        if (orgs) {
          const org = orgs.find((o) => o.id.toString() === practiceId);
          if (org) {
            orgName = org.name;
          }
        }
      });

    return orgName;
  }

  get tinStatus(): string {
    return this.currentPacket()?.tinDetails?.tinStatus || "pending";
  }

  get selectedTin(): string {
    return this.currentPacket()?.tinDetails?.tin || this.tinDetailsForm?.get("tin")?.value || "";
  }

  get isGroupReporting(): boolean {
    return this.reportingLevel === "group";
  }

  get isTinRollup(): boolean {
    return this.relatedOrganizations().length > 0;
  }

  get canApprove$(): Observable<boolean> {
    return this.isSuperAdmin$.pipe(
      map((isAdmin) => {
        const hasDocument = !!this.currentPacket()?.tinDetails?.documentName;
        return isAdmin && hasDocument;
      }),
    );
  }

  get canReject$(): Observable<boolean> {
    if (!this.tinDetailsForm) {
      return this.isSuperAdmin$.pipe(map(() => false));
    }

    return combineLatest([
      this.isSuperAdmin$,
      this.tinDetailsForm.statusChanges.pipe(
        startWith(this.tinDetailsForm.status),
      ),
      this.store.select(selectActivePacket),
    ]).pipe(
      map(([isAdmin, _, packet]) => {
        if (!isAdmin) return false;

        const hasDocument = !!packet?.tinDetails?.documentName;

        const emailControl = this.tinDetailsForm?.get("emailForFeedback");
        const confirmEmailControl = this.tinDetailsForm?.get(
          "confirmEmailForFeedback",
        );

        const emailValid = !!(emailControl?.valid && emailControl?.value);
        const confirmEmailValid = !!(
          confirmEmailControl?.valid && confirmEmailControl?.value
        );
        const emailsMatch = emailControl?.value === confirmEmailControl?.value;

        const hasValidEmails = emailValid && confirmEmailValid && emailsMatch;

        return hasDocument && hasValidEmails;
      }),
    );
  }

  showRejectEmailWarning(): boolean {
    if (this.tinStatus === "rejected") return false;
    if (!this.currentPacket()?.tinDetails?.documentName) return false;

    const emailControl = this.tinDetailsForm?.get("emailForFeedback");
    const confirmEmailControl = this.tinDetailsForm?.get(
      "confirmEmailForFeedback",
    );

    const emailValid = !!(emailControl?.valid && emailControl?.value);
    const confirmEmailValid = !!(
      confirmEmailControl?.valid && confirmEmailControl?.value
    );
    const emailsMatch = emailControl?.value === confirmEmailControl?.value;

    return !(emailValid && confirmEmailValid && emailsMatch);
  }

  getRejectButtonTooltip(): string {
    if (this.tinStatus === "rejected") {
      return "TIN has already been rejected";
    }

    if (!this.currentPacket()?.tinDetails?.documentName) {
      return "A document must be uploaded before rejection";
    }

    const emailControl = this.tinDetailsForm?.get("emailForFeedback");
    const confirmEmailControl = this.tinDetailsForm?.get(
      "confirmEmailForFeedback",
    );

    if (!emailControl?.value || !confirmEmailControl?.value) {
      return "Valid feedback email addresses are required for rejection";
    }

    if (emailControl?.value !== confirmEmailControl?.value) {
      return "Email addresses must match";
    }

    if (!emailControl?.valid || !confirmEmailControl?.valid) {
      return "Valid email addresses are required for rejection";
    }

    return "";
  }

  get canProceedToNextStep(): boolean {
    return this.tinDetailsForm?.valid;
  }

  get allProvidersSelected(): boolean {
    const currentSelections =
      this.tinDetailsForm.get("selectedProviders")?.value || [];
    return currentSelections.includes("all");
  }

  downloadDocument(): void {
    const packetId = this.currentPacket()?.id;
    if (!packetId) return;

    this.mipsApi.downloadTinDocument(packetId).subscribe({
      next: (blob) => {
        downloadBlob(
          blob,
          this.currentPacket()?.tinDetails?.documentName || "document"
        );
      },
      error: (error) => {
        console.error("Error downloading document:", error);
        alert("Failed to download document. Please try again.");
      },
    });
  }

  viewDocument(): void {
    const packetId = this.currentPacket()?.id;
    if (!packetId) return;

    this.mipsApi.downloadTinDocument(packetId).subscribe({
      next: (blob) => {
        openBlobInNewTab(blob);
      },
      error: (error) => {
        console.error("Error viewing document:", error);
        alert("Failed to view document. Please try again.");
      },
    });
  }

  ngOnInit(): void {
    const packet = this.currentPacket();
    if (packet?.practice) {
      this.previousPractice = packet.practice;
      this.store.dispatch(
        MipsPacketActions.loadProvidersForSelectedPractice({
          orgId: Number(packet.practice),
        }),
      );
    }

    this.store
      .select(selectActivePacket)
      .pipe(
        takeUntil(this.destroy$),
        map((p) => p?.practice),
      )
      .subscribe((practice) => {
        if (practice && practice !== this.previousPractice) {
          this.previousPractice = practice;
          this.store.dispatch(
            MipsPacketActions.loadProvidersForSelectedPractice({
              orgId: Number(practice),
            }),
          );
        }
      });

    this.filteredProviders$ = this.providerSearchControl.valueChanges.pipe(
      startWith(""),
      map((search) => {
        const allProviders = this.providers() || [];
        return this.filterProviders(search || "", allProviders);
      }),
    );

    this.tinDetailsForm = this.fb.group(
      {
        tin: [{ value: this.currentPacket()?.tinDetails?.tin || "", disabled: true }],
        practice: [{ value: this.practice, disabled: true }],
        selectedProviders: [["all"]],
        performanceStartDate: [
          this.getPerformanceStartDate(),
          Validators.required,
        ],
        performanceEndDate: [this.getPerformanceEndDate(), Validators.required],
        mipsEligible: [{ value: true, disabled: true }],
        emailForFeedback: ["", [Validators.required, Validators.email]],
        confirmEmailForFeedback: ["", [Validators.required, Validators.email]],
        supportingDocument: [null],
      },
      {
        validators: [this.emailMatchValidator(), this.providersValidator()],
      },
    );

    this.store
      .select(selectActivePacket)
      .pipe(takeUntil(this.destroy$))
      .subscribe((packet) => {
        if (packet?.id && packet.id !== this.currentPacketId) {
          this.currentPacketId = packet.id;

          if (packet.tinDetails) {
            this.loadFromPacket();
          } else {
            this.tinDetailsForm.patchValue(
              {
                tin: "",
                emailForFeedback: "",
                confirmEmailForFeedback: "",
                selectedProviders: ["all"],
              },
              { emitEvent: false },
            );
            this.uploadedFileName = null;
          }
        }
      });

    this.parentForm = this.ctrlContainer.form;
    if (this.parentForm) {
      this.parentForm.addControl("tinDetails", this.tinDetailsForm);
    }

    this.tinDetailsForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateParentFormValidity();
      });

    setTimeout(() => {
      this.updateParentFormValidity();
    });

    this.tinDetailsForm
      .get("selectedProviders")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        this.handleProviderSelection(newValue);
      });

    this.tinDetailsForm.valueChanges
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        const packet = this.currentPacket();
        if (this.tinDetailsForm.valid && packet?.id) {
          this.saveTinDetails();
        }
      });
  }

  private handleProviderSelection(newSelection: Array<string | number>): void {
    const previous = this.previousProviders;

    if (newSelection.includes("all") && !previous.includes("all")) {
      this.tinDetailsForm.patchValue(
        { selectedProviders: ["all"] },
        { emitEvent: false },
      );
      this.previousProviders = ["all"];
      return;
    }

    if (
      previous.includes("all") &&
      newSelection.includes("all") &&
      newSelection.length > 1
    ) {
      const withoutAll = newSelection.filter((id) => id !== "all");
      this.tinDetailsForm.patchValue(
        { selectedProviders: withoutAll },
        { emitEvent: false },
      );
      this.previousProviders = withoutAll;
      return;
    }

    if (newSelection.length === 0) {
      this.tinDetailsForm.patchValue(
        { selectedProviders: ["all"] },
        { emitEvent: false },
      );
      this.previousProviders = ["all"];
      return;
    }
    this.previousProviders = newSelection;
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("tinDetails");
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  isIndividualEligible(): boolean {
    return this.eligibilityResult()?.individualEligible ?? false;
  }

  isGroupEligible(): boolean {
    return this.eligibilityResult()?.groupEligible ?? false;
  }

  isSubgroupEligible(): boolean {
    return this.eligibilityResult()?.subgroupEligible ?? false;
  }

  private updateParentFormValidity(): void {
    if (this.parentForm) {
      const isValid = this.canProceedToNextStep;

      if (isValid) {
        this.parentForm.setErrors(null);
      } else {
        this.parentForm.setErrors({ incomplete: true });
      }
      this.cdr.markForCheck();
    }
  }

  private filterProviders(search: string, providers: any[]): any[] {
    const activeProviders = providers.filter((provider) => provider.active === 1);
    if (!search) {
      return activeProviders;
    }
    const searchLower = search.toLowerCase();
    return activeProviders.filter((provider) =>
      `${provider.firstName} ${provider.lastName} ${provider.npi}`
        .toLowerCase()
        .includes(searchLower),
    );
  }

  private loadFromPacket(): void {
    const tinDetails = this.currentPacket()?.tinDetails;
    if (!tinDetails) return;

    const parseDate = (
      dateString: string | Date | undefined,
    ): Date | undefined => {
      if (!dateString) return undefined;

      if (dateString instanceof Date) {
        return dateString;
      }

      if (typeof dateString === "string") {
        const parts = dateString.split("-");
        if (parts.length === 3) {
          return new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2]),
          );
        }
      }

      return new Date(dateString);
    };

    const selectedProviders =
      tinDetails.selectedProviders && tinDetails.selectedProviders.length > 0
        ? tinDetails.selectedProviders
        : ["all"];

    this.previousProviders = selectedProviders;

    this.tinDetailsForm.patchValue(
      {
        tin: tinDetails.tin,
        practice: tinDetails.practice,
        selectedProviders: selectedProviders,
        performanceStartDate: parseDate(tinDetails.performanceStartDate),
        performanceEndDate: parseDate(tinDetails.performanceEndDate),
        mipsEligible: tinDetails.mipsEligible,
        emailForFeedback: tinDetails.emailForFeedback,
        confirmEmailForFeedback: tinDetails.confirmEmailForFeedback,
      },
      { emitEvent: false },
    );

    if (tinDetails.documentName) {
      this.uploadedFileName = tinDetails.documentName;
      this.tinDetailsForm
        .get("supportingDocument")
        ?.setValue(tinDetails.documentName, { emitEvent: false });
      this.tinDetailsForm.get("supportingDocument")?.updateValueAndValidity();
    } else {
      this.uploadedFileName = null;
      this.tinDetailsForm
        .get("supportingDocument")
        ?.setValue(null, { emitEvent: false });
    }
  }

  private saveTinDetails(): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    const formValue = this.tinDetailsForm.getRawValue();

    const selectedProviders = formValue.selectedProviders.includes("all")
      ? []
      : formValue.selectedProviders;

    const tinDetails: TinEligibilityDetails = {
      tin: formValue.tin,
      confirmTin: formValue.tin,
      practice: this.practice,
      performanceStartDate: formValue.performanceStartDate,
      performanceEndDate: formValue.performanceEndDate,
      mipsEligible: true,
      emailForFeedback: formValue.emailForFeedback,
      confirmEmailForFeedback: formValue.confirmEmailForFeedback,
      selectedProviders: selectedProviders,
      documentName: this.uploadedFileName || undefined,
      documentUrl: packet.tinDetails?.documentUrl,
      tinStatus: packet.tinDetails?.tinStatus || "pending",
    };

    this.store.dispatch(
      MipsPacketActions.saveTINDetails({
        packetId: packet.id!,
        tinDetails,
      }),
    );
  }

  approveTin(): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    this.isSuperAdmin$.pipe(take(1)).subscribe((isSuperAdmin: any) => {
      if (!isSuperAdmin) return;

      this.store.dispatch(
        MipsPacketActions.approveTIN({
          packetId: packet.id!,
        }),
      );
    });
  }

  rejectTin(): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    this.isSuperAdmin$.pipe(take(1)).subscribe((isSuperAdmin: any) => {
      if (!isSuperAdmin) return;

      this.store.dispatch(
        MipsPacketActions.rejectTIN({
          packetId: packet.id!,
          reason: "",
        }),
      );
    });
  }

  providersValidator() {
    return (formGroup: FormGroup) => {
      if (this.isGroupReporting && !this.isTinRollup) {
        const providers = formGroup.get("selectedProviders");
        const value = providers?.value || [];

        if (value.length === 0) {
          providers?.setErrors({ required: true });
        } else {
          if (providers?.hasError("required")) {
            providers.setErrors(null);
          }
        }
      }
      return null;
    };
  }

  emailMatchValidator() {
    return (formGroup: FormGroup) => {
      const email = formGroup.get("emailForFeedback");
      const confirmEmail = formGroup.get("confirmEmailForFeedback");

      if (email && confirmEmail && email.value !== confirmEmail.value) {
        confirmEmail.setErrors({ emailMismatch: true });
      } else {
        if (confirmEmail?.hasError("emailMismatch")) {
          confirmEmail.setErrors(null);
        }
      }
      return null;
    };
  }

  getPerformanceStartDate(): Date {
    return new Date(this.performanceYear, 0, 1);
  }

  getPerformanceEndDate(): Date {
    return new Date(this.performanceYear, 11, 31);
  }

  toggleAllProviders(): void {
    const current = this.tinDetailsForm.get("selectedProviders")?.value || [];
    if (current.includes("all")) {
      this.tinDetailsForm.patchValue({ selectedProviders: [] });
    } else {
      this.tinDetailsForm.patchValue({ selectedProviders: ["all"] });
    }
  }


  providerClicked(): void {
    setTimeout(() => {
      const currentSelections =
        this.tinDetailsForm.get("selectedProviders")?.value || [];

      if (currentSelections.includes("all") && currentSelections.length > 1) {
        const filtered = currentSelections.filter((id: string) => id !== "all");
        this.tinDetailsForm.patchValue({ selectedProviders: filtered });
      }

      if (currentSelections.length === 0) {
        this.tinDetailsForm.patchValue({ selectedProviders: ["all"] });
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = MAX_FILE_SIZE;
    if (file.size > maxSize) {
      alert("File size exceeds 25MB limit");
      event.target.value = "";
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Acceptable formats: PDF, JPG, PNG, JPEG");
      event.target.value = "";
      return;
    }

    this.uploadedFileName = file.name;
    this.tinDetailsForm.patchValue({ supportingDocument: file.name });

    this.uploadFile(file);
    event.target.value = "";
  }

  private uploadFile(file: File): void {
    const packet = this.currentPacket();
    if (!packet?.id) return;

    this.uploadingDocument = true;

    this.store.dispatch(
      MipsPacketActions.uploadTINDocument({
        packetId: packet.id!,
        file,
      }),
    );
  }

  removeFile(): void {
    this.uploadedFileName = null;
    this.tinDetailsForm.patchValue({ supportingDocument: null });

    const packet = this.currentPacket();
    if (packet?.id) {
      this.store.dispatch(
        MipsPacketActions.deleteTINDocument({
          packetId: packet.id!,
        }),
      );
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.tinDetailsForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  openQppSite(): void {
    const npi = this.npi;
    const year = this.performanceYear;
    window.open(
      `https://qpp.cms.gov/participation-lookup?npi=${npi}&py=${year}`,
      "_blank",
    );
  }

  navigateToEligibilityStep(): void {
    this.store.dispatch(MipsPacketActions.navigateToStep({ stepIndex: 1 }));
  }

  getTinStatusText(): string {
    switch (this.tinStatus) {
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "pending":
      default:
        return "Pending Approval";
    }
  }

  getTinStatusClass(): string {
    switch (this.tinStatus) {
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "pending":
      default:
        return "status-pending";
    }
  }
}
